import DOMPurify from 'dompurify';

export const formatAnswer = (text) => {
  if (!text) return '';
  
  let html = text;
  
  // Headers
  html = html.replace(/^### (.+?)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+?)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+?)$/gm, '<h1>$1</h1>');
  
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');
  
  // Code blocks - MUST be done BEFORE other processing
  html = html.replace(/```([\s\S]*?)```/g, (match, code) => {
    return `<CODEBLOCK>${code}</CODEBLOCK>`;
  });
  
  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // Blockquotes
  html = html.replace(/^> (.+?)$/gm, '<blockquote>$1</blockquote>');
  
  // Process lists and paragraphs - convert bullet/numbered lists to HTML
  const lines = html.split('\n');
  let processedHtml = [];
  let inUnorderedList = false;
  let inOrderedList = false;
  let inCodeBlock = false;
  let codeBlockContent = [];
  let orderCounter = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    // Handle code blocks specially
    if (trimmed.includes('<CODEBLOCK>')) {
      inCodeBlock = true;
      codeBlockContent = [trimmed.replace('<CODEBLOCK>', '')];
      continue;
    }
    
    if (trimmed.includes('</CODEBLOCK>')) {
      inCodeBlock = false;
      codeBlockContent.push(trimmed.replace('</CODEBLOCK>', ''));
      // Close any open list before code block
      if (inUnorderedList) {
        processedHtml.push('</ul>');
        inUnorderedList = false;
      }
      if (inOrderedList) {
        processedHtml.push('</ol>');
        inOrderedList = false;
        orderCounter = 0;
      }
      const codeContent = codeBlockContent.join('\n').trim();
      processedHtml.push(`<pre><code>${codeContent}</code></pre>`);
      continue;
    }
    
    if (inCodeBlock) {
      codeBlockContent.push(line); // Preserve original line (with indentation)
      continue;
    }
    
    // Check if line is a list item
    const isUnorderedItem = /^[-•*]\s+/.test(trimmed);
    const isOrderedItem = /^\d+\.\s+/.test(trimmed);
    
    if (isUnorderedItem) {
      if (!inUnorderedList) {
        processedHtml.push('<ul>');
        inUnorderedList = true;
      }
      if (inOrderedList) {
        processedHtml.push('</ol>');
        inOrderedList = false;
        orderCounter = 0;
      }
      const itemText = trimmed.replace(/^[-•*]\s+/, '');
      processedHtml.push(`<li>${itemText}</li>`);
    } else if (isOrderedItem) {
      if (!inOrderedList) {
        processedHtml.push('<ol>');
        inOrderedList = true;
        orderCounter = 0;
      }
      if (inUnorderedList) {
        processedHtml.push('</ul>');
        inUnorderedList = false;
      }
      orderCounter++;
      const itemText = trimmed.replace(/^\d+\.\s+/, '');
      processedHtml.push(`<li>${itemText}</li>`);
    } else {
      // Close any open list
      if (inUnorderedList) {
        processedHtml.push('</ul>');
        inUnorderedList = false;
      }
      if (inOrderedList) {
        processedHtml.push('</ol>');
        inOrderedList = false;
        orderCounter = 0;
      }
      
      // Handle regular text or already formatted HTML
      if (trimmed) {
        if (trimmed.match(/^<[^>]/)) {
          // Already HTML-formatted (headers, blockquotes, inline code)
          processedHtml.push(trimmed);
        } else {
          // Plain text - wrap in paragraph
          processedHtml.push(`<p>${trimmed}</p>`);
        }
      }
    }
  }
  
  // Close any remaining open lists
  if (inUnorderedList) {
    processedHtml.push('</ul>');
  }
  if (inOrderedList) {
    processedHtml.push('</ol>');
  }
  
  html = processedHtml.join('\n');
  
  // SECURITY: Sanitize HTML to prevent XSS
  return DOMPurify.sanitize(html, { 
    ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'strong', 'em', 'u', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'br'],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  });
};

export const formatAsList = (text) => {
  const lines = text.split('\n');
  let html = '';
  let inList = false;

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.match(/^[-•*\d.]/)) {
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      const cleaned = trimmed.replace(/^[-•*]\s+|^\d+\.\s+/, '');
      html += `<li>${DOMPurify.sanitize(cleaned)}</li>`;
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      if (trimmed) html += `<p>${DOMPurify.sanitize(trimmed)}</p>`;
    }
  });

  if (inList) html += '</ul>';
  return html;
};

export const createSummaryBox = (title, points) => {
  let html = `<div class="summary-box"><h3>${DOMPurify.sanitize(title)}</h3><ul>`;
  points.forEach(point => {
    html += `<li>${DOMPurify.sanitize(point)}</li>`;
  });
  html += '</ul></div>';
  return html;
};

export const highlightKeyTerms = (text, terms) => {
  let html = DOMPurify.sanitize(text);
  terms.forEach(term => {
    const regex = new RegExp(`\\b${term}\\b`, 'gi');
    html = html.replace(regex, `<span class="highlight-badge">${DOMPurify.sanitize(term)}</span>`);
  });
  return html;
};
